import { useState } from 'react';
import { Shield, KeyRound, AlertTriangle, ArrowLeft } from 'lucide-react';
import Button from '../common/Button';
import Input from '../common/Input';
import { loginWithMfa } from '../../api/mfaApi';

interface MfaVerificationProps {
  mfaSessionToken: string;
  email: string;
  expiresAt: string;
  onSuccess: (authResponse: unknown) => void;
  onCancel: () => void;
}

export default function MfaVerification({
  mfaSessionToken,
  email,
  expiresAt,
  onSuccess,
  onCancel,
}: MfaVerificationProps) {
  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLockedOut, setIsLockedOut] = useState(false);
  const [lockedUntil, setLockedUntil] = useState<string | null>(null);

  const isExpired = new Date(expiresAt) < new Date();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isExpired) {
      setError('Your session has expired. Please log in again.');
      return;
    }

    const trimmedCode = code.trim();
    if (useBackupCode) {
      if (trimmedCode.length < 8) {
        setError('Please enter a valid backup code');
        return;
      }
    } else {
      if (trimmedCode.length !== 6 || !/^\d+$/.test(trimmedCode)) {
        setError('Please enter a 6-digit code');
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const response = await loginWithMfa(mfaSessionToken, trimmedCode, useBackupCode);
      onSuccess(response);
    } catch (err: unknown) {
      const errorData = (err as { response?: { data?: { message?: string; isLockedOut?: boolean; lockedUntil?: string } } })?.response?.data;

      if (errorData?.isLockedOut) {
        setIsLockedOut(true);
        setLockedUntil(errorData.lockedUntil || null);
        setError('Your account has been temporarily locked due to too many failed attempts.');
      } else {
        setError(errorData?.message || 'Invalid code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (useBackupCode) {
      // Allow alphanumeric for backup codes
      setCode(value.toUpperCase().replace(/[^A-Z0-9]/g, ''));
    } else {
      // Only digits for TOTP
      setCode(value.replace(/\D/g, ''));
    }
    setError(null);
  };

  const toggleBackupCode = () => {
    setUseBackupCode(!useBackupCode);
    setCode('');
    setError(null);
  };

  if (isExpired) {
    return (
      <div className="text-center space-y-4">
        <div className="mx-auto w-16 h-16 bg-error/10 rounded-full flex items-center justify-center">
          <AlertTriangle className="w-8 h-8 text-error" />
        </div>
        <h2 className="text-xl font-semibold text-text">Session Expired</h2>
        <p className="text-text-secondary">
          Your MFA session has expired. Please log in again.
        </p>
        <Button onClick={onCancel} className="w-full">
          Back to Login
        </Button>
      </div>
    );
  }

  if (isLockedOut) {
    return (
      <div className="text-center space-y-4">
        <div className="mx-auto w-16 h-16 bg-error/10 rounded-full flex items-center justify-center">
          <AlertTriangle className="w-8 h-8 text-error" />
        </div>
        <h2 className="text-xl font-semibold text-text">Account Locked</h2>
        <p className="text-text-secondary">
          Your account has been temporarily locked due to too many failed verification attempts.
        </p>
        {lockedUntil && (
          <p className="text-sm text-text-muted">
            Try again after {new Date(lockedUntil).toLocaleTimeString()}
          </p>
        )}
        <Button onClick={onCancel} className="w-full">
          Back to Login
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
          {useBackupCode ? (
            <KeyRound className="w-8 h-8 text-primary" />
          ) : (
            <Shield className="w-8 h-8 text-primary" />
          )}
        </div>
        <h2 className="text-xl font-semibold text-text">
          {useBackupCode ? 'Enter Backup Code' : 'Two-Factor Authentication'}
        </h2>
        <p className="text-text-secondary text-sm">
          {useBackupCode
            ? 'Enter one of your backup codes'
            : `Enter the 6-digit code from your authenticator app for ${email}`}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-error flex-shrink-0 mt-0.5" />
          <p className="text-sm text-error">{error}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Input
            type="text"
            inputMode={useBackupCode ? 'text' : 'numeric'}
            pattern={useBackupCode ? '[A-Z0-9]*' : '[0-9]*'}
            maxLength={useBackupCode ? 10 : 6}
            value={code}
            onChange={handleCodeChange}
            placeholder={useBackupCode ? 'XXXXXXXX' : '000000'}
            className="text-center text-2xl tracking-widest font-mono"
            autoFocus
            autoComplete="one-time-code"
          />
        </div>

        <Button
          type="submit"
          disabled={loading || (useBackupCode ? code.length < 8 : code.length !== 6)}
          className="w-full"
        >
          {loading ? 'Verifying...' : 'Verify'}
        </Button>
      </form>

      {/* Toggle backup code */}
      <div className="text-center">
        <button
          type="button"
          onClick={toggleBackupCode}
          className="text-sm text-primary hover:text-primary-dark transition-colors"
        >
          {useBackupCode
            ? 'Use authenticator app instead'
            : "Can't access your authenticator? Use a backup code"}
        </button>
      </div>

      {/* Back to login */}
      <div className="pt-4 border-t border-border">
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-2 text-sm text-text-secondary hover:text-text transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </button>
      </div>
    </div>
  );
}
